// @ai-generated 2026-09-22T14:57:27+00:00 by niklas.eicker@tngtech.com using Claude Opus 5 (1M context)
import { STAMMDATEN_API } from '@Constants/backendConstants';
import { Permission } from '@Constants/Permission.g';
import { useHasPermission } from '@Contexts/appContext';
import { useIsPortalSession } from '@Contexts/session';
import { useIdLookup } from '@Hooks/useIdLookup';
import { type LinkableField, useServerListApi } from '@synqo/frontend-component-library';
import { useCallback } from 'react';

interface RecordWithPortalName {
    id: string;
    external_label: string;
}

/** The names of the records a set of links points at. */
export interface PortalLabels {
    /** Asks for the names behind the given links. Does nothing outside a portal session. */
    ensure: (links: readonly (LinkableField | undefined)[]) => void;
    /**
     * What to call the linked record here: the name the operator picked for the portal where the
     * session is a customer's and the record has one, the name the link carries otherwise.
     */
    labelOf: (link: LinkableField | undefined) => string;
}

/**
 * The portal names of master-data records that links only name by id.
 */
function usePortalLabels(path: string, readPermission: Permission): PortalLabels {
    const isPortalSession = useIsPortalSession();
    const hasPermission = useHasPermission();
    const { loadBatch } = useServerListApi<RecordWithPortalName>({ path });

    const canReadNames = isPortalSession && hasPermission(readPermission);

    const loadNames = useCallback(
        async (ids: string[]) => {
            const loaded = await loadBatch({
                page_no: 0,
                page_size: ids.length,
                full_text_search: '',
                sort_columns: [],
                include_ids: ids,
                exclude_ids: [],
            });

            return loaded.items.map((item) => [item.id, item.external_label] as const);
        },
        [loadBatch],
    );

    const { valuesById, request } = useIdLookup(loadNames);

    const ensure = useCallback(
        (links: readonly (LinkableField | undefined)[]) => {
            if (!canReadNames) {
                return;
            }

            request(links.map((link) => link?.foreign_key));
        },
        [canReadNames, request],
    );

    const labelOf = useCallback(
        (link: LinkableField | undefined) => {
            if (!link) {
                return '';
            }

            const portalName = canReadNames ? valuesById.get(link.foreign_key ?? '') : undefined;

            return portalName || link.display_string;
        },
        [canReadNames, valuesById],
    );

    return { ensure, labelOf };
}

/** The portal names of the services behind service links, e.g. the material of a position. */
export function usePortalServiceLabels(): PortalLabels {
    return usePortalLabels(`${STAMMDATEN_API}/services`, Permission.StammdatenServiceRead);
}

/** The portal names of the container types behind container-type links. */
export function usePortalContainerTypeLabels(): PortalLabels {
    return usePortalLabels(`${STAMMDATEN_API}/container_types`, Permission.StammdatenContainerTypeRead);
}
